// Universal site-search results modal.
//
// Rendered by the navbar search. Displays results grouped into collapsible
// per-type sections (Organizations, Events, Regulations, Articles). The first
// section that has results starts expanded; the rest start collapsed. An
// expanded section scrolls within itself so every other section header stays
// visible without scrolling the modal. A "One list" toggle flattens all
// results into a single ranked list (most matched fields first, then
// alphabetical) with a type chip per row. Empty sections are hidden. Each
// result row shows the page title plus a chip per matched field; hovering a
// chip reveals a tooltip with a context snippet. Clicking a row opens the
// detail page in a new tab.

import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Dialog, Divider, FormControlLabel, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { blue, green, orange, purple, common, gray } from "@/theme/colors";
import { assetUrl } from "@/utils/asset";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TYPE_COLOR = {
  organization: orange.primary,
  event: green.primary,
  regulation: blue.primary,
  article: purple.primary
};
const TYPE_LABEL = {
  organization: "Organization",
  event: "Event",
  regulation: "Regulation",
  article: "Article"
};
function openInNewTab(url) {
  if (typeof window === "undefined") return;
  window.open(assetUrl(url), "_blank", "noopener,noreferrer");
}

/** One result row — shared by the sectioned and flat layouts. */
function ResultRow({
  result,
  color,
  showTypeChip = false
}) {
  return /*#__PURE__*/_jsxs(Box, {
    onClick: () => openInNewTab(result.url),
    sx: {
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "minmax(180px, 32%) 1fr auto"
      },
      alignItems: "center",
      gap: 1.5,
      px: 1.5,
      py: 1.25,
      cursor: "pointer",
      border: `1px solid ${color}55`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 1,
      "&:hover": {
        backgroundColor: `${color}0d`,
        borderColor: color,
        borderLeftColor: color
      }
    },
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        minWidth: 0
      },
      children: [showTypeChip && /*#__PURE__*/_jsx(Chip, {
        label: TYPE_LABEL[result.type],
        size: "small",
        sx: {
          backgroundColor: color,
          color: common.white,
          fontWeight: 600,
          height: 18,
          fontSize: "0.65rem",
          mb: 0.25
        }
      }), /*#__PURE__*/_jsx(Typography, {
        sx: {
          fontWeight: 600,
          color: "text.primary",
          overflow: "hidden",
          textOverflow: "ellipsis"
        },
        children: result.title
      })]
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75
      },
      children: result.matchedFields.map((field, i) => /*#__PURE__*/_jsx(Tooltip, {
        arrow: true,
        title: /*#__PURE__*/_jsxs(Box, {
          sx: {
            maxWidth: 320
          },
          children: [/*#__PURE__*/_jsx(Typography, {
            sx: {
              fontWeight: 700,
              fontSize: "0.75rem",
              mb: 0.5
            },
            children: field.label
          }), /*#__PURE__*/_jsx(Typography, {
            sx: {
              fontSize: "0.75rem"
            },
            children: field.snippet
          })]
        }),
        children: /*#__PURE__*/_jsx(Chip, {
          label: field.label,
          size: "small",
          onClick: e => e.stopPropagation(),
          sx: {
            backgroundColor: `${color}1f`,
            color,
            fontWeight: 600,
            cursor: "pointer"
          }
        })
      }, `${field.label}-${i}`))
    }), /*#__PURE__*/_jsx(OpenInNewIcon, {
      fontSize: "small",
      sx: {
        color: "text.secondary",
        display: {
          xs: "none",
          sm: "block"
        }
      }
    })]
  });
}
export default function SiteSearchModal({
  open,
  onClose,
  term,
  sections,
  loading
}) {
  // Only sections that actually have results are shown.
  const visibleSections = useMemo(() => sections.filter(s => s.results.length > 0), [sections]);
  const totalResults = useMemo(() => visibleSections.reduce((sum, s) => sum + s.results.length, 0), [visibleSections]);

  // Flat mode: every result in one ranked list (most matched fields first,
  // then alphabetical) instead of per-type sections.
  const [flatList, setFlatList] = useState(false);
  const flatResults = useMemo(() => visibleSections.flatMap(s => s.results).sort((a, b) => b.matchedFields.length - a.matchedFields.length || a.title.localeCompare(b.title)), [visibleSections]);

  // Track which sections are expanded by type. Default: first non-empty
  // section open, the rest collapsed. Recomputed whenever the result set
  // changes (e.g. a new search while the modal stays open).
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const next = {};
    visibleSections.forEach((section, index) => {
      next[section.type] = index === 0;
    });
    setExpanded(next);
  }, [visibleSections]);
  const toggleSection = type => {
    setExpanded(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  return /*#__PURE__*/_jsxs(Dialog, {
    open: open,
    onClose: onClose,
    maxWidth: false,
    scroll: "paper",
    PaperProps: {
      sx: {
        width: "clamp(400px,50vw,600px)",
        m: 0,
        maxHeight: "85vh",
        borderRadius: 2
      }
    },
    sx: {
      "& .MuiDialog-container": {
        alignItems: "flex-start",
        paddingTop: "50px"
      }
    },
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 3,
        py: 2,
        backgroundColor: gray.dark,
        color: common.white
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        children: [/*#__PURE__*/_jsx(Typography, {
          sx: {
            fontWeight: 700,
            fontSize: "1.1rem"
          },
          children: "Search results"
        }), term ? /*#__PURE__*/_jsx(Typography, {
          sx: {
            fontSize: "0.85rem",
            opacity: 0.85
          },
          children: loading ? `Searching for “${term}”…` : `${totalResults} result${totalResults === 1 ? "" : "s"} for “${term}”`
        }) : null]
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          gap: 1
        },
        children: [/*#__PURE__*/_jsx(FormControlLabel, {
          control: /*#__PURE__*/_jsx(Switch, {
            size: "small",
            checked: flatList,
            onChange: e => setFlatList(e.target.checked),
            sx: {
              "& .MuiSwitch-track": {
                backgroundColor: common.white
              }
            }
          }),
          label: "One list",
          sx: {
            mr: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "0.8rem",
              color: common.white
            }
          }
        }), /*#__PURE__*/_jsx(IconButton, {
          onClick: onClose,
          size: "small",
          sx: {
            color: common.white
          },
          "aria-label": "close search results",
          children: /*#__PURE__*/_jsx(CloseIcon, {})
        })]
      })]
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        overflowY: "auto",
        px: 3,
        py: 2,
        minHeight: 160
      },
      children: loading ? /*#__PURE__*/_jsx(Box, {
        sx: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200
        },
        children: /*#__PURE__*/_jsx(CircularProgress, {})
      }) : visibleSections.length === 0 ? /*#__PURE__*/_jsx(Box, {
        sx: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200
        },
        children: /*#__PURE__*/_jsx(Typography, {
          color: "text.secondary",
          children: "No results found"
        })
      }) : flatList ? /*#__PURE__*/_jsx(Box, {
        sx: {
          display: "flex",
          flexDirection: "column",
          gap: 0.75
        },
        children: flatResults.map(result => /*#__PURE__*/_jsx(ResultRow, {
          result: result,
          color: TYPE_COLOR[result.type],
          showTypeChip: true
        }, `${result.type}-${result.id}`))
      }) : visibleSections.map(section => {
        const color = TYPE_COLOR[section.type];
        const isOpen = expanded[section.type] ?? false;
        return /*#__PURE__*/_jsxs(Box, {
          sx: {
            mb: 2
          },
          children: [/*#__PURE__*/_jsxs(Box, {
            onClick: () => toggleSection(section.type),
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              px: 1.5,
              py: 1,
              borderRadius: 1,
              backgroundColor: `${color}14`,
              borderLeft: `4px solid ${color}`,
              "&:hover": {
                backgroundColor: `${color}22`
              }
            },
            children: [isOpen ? /*#__PURE__*/_jsx(ExpandLessIcon, {
              sx: {
                color
              },
              fontSize: "small"
            }) : /*#__PURE__*/_jsx(ExpandMoreIcon, {
              sx: {
                color
              },
              fontSize: "small"
            }), /*#__PURE__*/_jsx(Typography, {
              sx: {
                fontWeight: 700,
                color,
                flexGrow: 1
              },
              children: section.label
            }), /*#__PURE__*/_jsx(Chip, {
              size: "small",
              label: section.results.length,
              sx: {
                backgroundColor: color,
                color: common.white,
                fontWeight: 700,
                height: 22
              }
            })]
          }), isOpen && /*#__PURE__*/_jsx(Box, {
            sx: {
              mt: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              maxHeight: "42vh",
              overflowY: "auto",
              pr: 0.5
            },
            children: section.results.map(result => /*#__PURE__*/_jsx(ResultRow, {
              result: result,
              color: color
            }, `${result.type}-${result.id}`))
          }), /*#__PURE__*/_jsx(Divider, {
            sx: {
              mt: 1
            }
          })]
        }, section.type);
      })
    })]
  });
}